import { Component, OnInit, inject } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { LoaderService } from '../../../core/components/loader/loader.service';
import { DriverDetailsCacheService } from '../../../features/monitoring-center/services/driver-details-cache.service';
import { getFormattedDate } from '../../../shared/utilities/utility-functions';
import { QueryConstraint, where } from 'firebase/firestore';
import { RideEmergencyAlert } from '../../../shared/interfaces/ride-emergency-alert';
import { RideDocumentDto } from '../../../shared/interfaces/ride-firestore';
import { RideListItem } from '../../../shared/interfaces/ride-list-item';
import { FireStoreService } from '../../../shared/services/firestore.service';
import { EnvConfigService } from '../../../shared/services/env-config.service';
import { ActionType } from '../../../shared/enums/common';
import moment from 'moment';
import { MapRideServiceService } from '../../../features/monitoring-center/services/map-ride-service.service';
import { ActionDialogService } from '../../../features/monitoring-center/services/action-dialog.service';
import { UserRole } from '../../../core/auth/enums/userRoles';
import { RideActionsService } from '../../../features/monitoring-center/services/ride-actions.service';

@Component({
  selector: 'app-emergency-alert',
  standalone: true,
  imports: [ToastModule],
  providers: [DialogService, MessageService],
  templateUrl: './emergency-alert.component.html',
  styleUrl: './emergency-alert.component.scss',
})
export class EmergencyAlertComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly loaderService = inject(LoaderService);
  private readonly driverDetailsCacheService = inject(DriverDetailsCacheService);
  private readonly firestoreService = inject(FireStoreService);
  readonly env = inject(EnvConfigService);
  private readonly mapRideService = inject(MapRideServiceService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly dialogService = inject(DialogService);
  private readonly rideActionsService = inject(RideActionsService);

  rideEmergencyDocs: any = [];
  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();

  ngOnInit() {
    this.getRideEmergencyAlerts();
  }

  getRideEmergencyAlerts() {
    const date = new Date();
    const formattedDate = getFormattedDate(date);
    const filters: QueryConstraint[] = [where('is_acknowledged', '==', false)];

    this.firestoreService.listenToCollectionChanges(`${this.env.getConfig().emergenciesCollection}/date_${formattedDate}/alerts`, filters).subscribe({
      next: res => {
        console.log(res);

        const changes: { id: string; type: string; data: RideEmergencyAlert }[] = res.changes.sort((a: any, b: any) => a.data.time.seconds - b.data.time.seconds);
        changes.forEach(async change => {
          if (change.type == ActionType.added) {
            this.rideEmergencyDocs.push({ docId: change.id, data: change.data });
            console.log(this.rideEmergencyDocs, change.id);

            // timeout to make sure the html element is set
            await this.delay(100);
            this.showRideEmergencyAlert(change.data, change.id);
          }
          if (change.type == ActionType.removed) {
            const currentEmergencyDocIdIndex = this.rideEmergencyDocs.findIndex((doc: any) => change.id == doc.docId);
            if (currentEmergencyDocIdIndex != -1) this.rideEmergencyDocs.splice(currentEmergencyDocIdIndex, 1);

            this.messageService.clear('emergencyAlert');

            await this.delay(100);
            this.rideEmergencyDocs.forEach((doc: any) => {
              this.showRideEmergencyAlert(doc.data, doc.docId);
            });
          }
        });
      },
      error: err => {
        console.log(err);
      },
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  showRideEmergencyAlert(data: RideEmergencyAlert, docId: string) {
    const time: string = moment.unix(data.time.seconds).format('h:mm A');
    const cameraEmergencyDescription = `Required camera in ride <span class="text-info-color" >${data.ride_id}</span> needs reassignment`;
    const emergencyDescription: string = `${data.driver_name} has reported ${data.emergency_type} on ride ${data.ride_id} at ${time}.`;
    const description: string = data.emergency_type == 'RequiredCamera' ? cameraEmergencyDescription : emergencyDescription;

    this.messageService.add({
      severity: data.emergency_type == 'RequiredCamera' ? 'warning' : 'danger',
      summary: data.emergency_type == 'RequiredCamera' ? 'Warning' : 'Emergency Alert!',
      detail: description,
      icon: 'icon-warning',
      styleClass: 'emergency-alert',
      sticky: true,
      key: `emergencyAlert`,
      closable: data.emergency_type == 'RequiredCamera',
      data: { doc_id: docId, data },
    });
  }

  onRideEmergencyClick(message: ToastMessageOptions) {
    console.log(message);
    this.getCurrentRideById(message.data.data.ride_id, message.detail ?? '', message.data.doc_id, message.data.data.emergency_type);
  }

  getCurrentRideById(rideId: number, emergencyDetails: string, emergencyDocId: string, emergencyType: string) {
    this.loaderService.show();

    const filters: QueryConstraint[] = [where('original_ride_id', '==', rideId)];
    this.firestoreService
      .getDataFromCollection(this.env.getConfig().ridesCollection, filters)
      .then(res => {
        if (res[0]) {
          const mappedRide: RideListItem = this.mapRideService.getMappedItem(res[0].data as RideDocumentDto);
          this.loaderService.hide();
          if (emergencyType == 'RequiredCamera') {
            // Acknowledge the emergency
            const date = new Date();
            const formattedDate = getFormattedDate(date);
            this.firestoreService
              .updateDocField(`${this.env.getConfig().emergenciesCollection}/date_${formattedDate}/alerts`, emergencyDocId, { is_acknowledged: true })
              .then((res: any) => {
                console.log(res);
              })
              .catch((err: any) => {
                console.log(err);
              });

            // Open assign sub-driver dialog
            if (this.currentRole == UserRole.Provider) {
              this.rideActionsService.openProviderAssignSubDriverDialog(mappedRide, res[0].data as RideDocumentDto, this.dialogService, this.messageService);
            } else {
              this.rideActionsService.openAssignSubDriverDialog(mappedRide, res[0].data as RideDocumentDto, this.dialogService, this.messageService);
            }
          } else {
            let mapDialogData: any = {
              dialogService: this.dialogService,
              docRide: res[0].data as RideDocumentDto,
              mappedRide: mappedRide,
              emergencyDialog: true,
              emergencyDetails,
              emergencyDocId,
            };
            // Get driver availability status
            this.driverDetailsCacheService.getAvailabilityStatus(mappedRide.driverDetails?.id ?? 0).subscribe({
              next: isOnline => {
                mapDialogData.driverOnline = isOnline;
                this.actionDialogService.openMapDialog(mapDialogData);
              },
              error: err => {
                console.log(err);
                mapDialogData.driverOnline = false;
                this.actionDialogService.openMapDialog(mapDialogData);
              },
            });
          }
        }
      })
      .catch(err => {
        this.loaderService.hide();
        console.log(err);
      });
  }
}
